import { React, useState, useEffect, useContext } from 'react';
import UserContext from '../../../components/User_Context.js';
import { dms_style } from "../../../styles/style.js";
import * as menu from "../../../assets/menus/menu.js";
import * as api from "../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Choices from '../../../components/Choices.js';
import { formBuilderLabels } from '../../_label/form_builder/form_builder_labels.js';
import * as breadcrumb from "../../../components/ui/breadcrumb.js";

function Form_Builder_Finder(params) {
    const { currentUser } = useContext(UserContext);

    // UI state variables
    const [searchList, setSearchList] = useState(null);
    const [refresh, setRefresh] = useState(null);

    // Form input states
    const [formName, setFormName] = useState("");
    const [formCode, setFormCode] = useState("");
    const [formGroup, setFormGroup] = useState("");
    const [groupsVoc, setGroupsVoc] = useState([]);


    const { t } = useTranslation(); // Translation hook
    const MySwal = withReactContent(Swal); // SweetAlert instance

    // Set breadcrumb using centralized component
    const setBreadCrumb = () => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/finder", name: t('formBuilderFinder', { ns: 'labels' }) }
        ];
        breadcrumb.set(path);
    };

    // Initialize translations and set up component
    useEffect(() => {
        (async () => {
            // First initialize translations
            const i18n = menu.getI18n();
            i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
            i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);

            // Then set breadcrumb with translations available
            setBreadCrumb();
            menu.write({ user: currentUser() });

            // Enable page scroll by overriding body overflow
            document.body.style.overflow = 'auto';
        })();

        // Cleanup: restore original overflow when component unmounts
        return () => {
            document.body.style.overflow = 'hidden';
        };
    }, []);

    // Load vocabulary for form groups
    useEffect(() => {
        const loadGroups = async () => {
            try {
                const groupsResult = await api.sendMessage("admin/vocs/get", {
                    user: currentUser(),
                    body: {
                        voc_id: "form_groups"
                    }
                });

                if (groupsResult?.items) {
                    setGroupsVoc(groupsResult.items);
                }
            } catch (err) {
                console.error("Error loading groups:", err);
            }
        };

        loadGroups();
    }, [currentUser]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') getFilteredData();
    };

    // Fetch filtered form data from API based on search inputs
    const getFilteredData = async () => {
        const result = await api.sendMessage("darm/formBuilder/finder", {
            user: currentUser(),
            body: {
                formName,
                formCode,
                formGroup
            }
        });

        setSearchList(result.filtered);
        setRefresh(new Date()); // Trigger UI update
    };

    // Reset all search filters
    const clearData = () => {
        setFormName("");
        setFormCode("");
        setFormGroup("");
        setSearchList(null);
    };

    // Format FormId as zero-padded 5-digit string (e.g. 23 → "00023")
    const formatFormId = (id) => String(id ?? '').padStart(5, '0');

    // Format ISO date string to dd/mm/yyyy
    const formatDate = (isoString) => {
        if (!isoString) return "";
        const date = new Date(isoString);
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    };

    // Handle form deletion with confirmation dialog
    const handleDeleteForm = (id, formName) => {
        MySwal.fire({
            title: t('attention', { ns: 'labels' }),
            text: t('confirmDelete', { ns: 'labels' }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#f05f23',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('confirm', { ns: 'labels' }),
            cancelButtonText: t('cancel', { ns: 'labels' })
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await api.sendMessage("darm/formBuilder/delete", {
                        user: currentUser(),
                        body: {
                            id: id
                        }
                    });

                    if (response.success) {
                        MySwal.fire({
                            title: t('success', { ns: 'labels' }),
                            text: t('deleteSuccess', { ns: 'labels' }),
                            icon: 'success',
                            timer: 2000
                        });
                        // Refresh the list after delete
                        getFilteredData();
                    } else {
                        MySwal.fire({
                            title: t('error', { ns: 'labels' }),
                            text: response.message || t('deleteError', { ns: 'labels' }),
                            icon: 'error'
                        });
                    }
                } catch (error) {
                    MySwal.fire({
                        title: t('error', { ns: 'labels' }),
                        text: t('deleteError', { ns: 'labels' }),
                        icon: 'error'
                    });
                }
            }
        });
    };

    // Render a row in the results table
    const getTableRow = (item) => (
        <tr key={item.Id} style={{ ...dms_style.table_row }}>
            <td
                onClick={() => window.location.href = `/form_builder/${item.Id}`}
                style={{
                    cursor: 'pointer',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '200px'
                }}
            > {formatFormId(item.FormId)}
            </td>
            <td
                onClick={() => window.location.href = `/form_builder/${item.Id}`}
                style={{
                    cursor: 'pointer',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '200px'
                }}
            >
                {item.FormName}
            </td>
            <td
                onClick={() => window.location.href = `/form_builder/${item.Id}`}
                style={{
                    cursor: 'pointer',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '150px'
                }}
            >
                {item.Code}
            </td>
            <td
                onClick={() => window.location.href = `/form_builder/${item.Id}`}
                style={{
                    cursor: 'pointer',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '150px'
                }}
            >
                {item.Group}
            </td>
            <td
                onClick={() => window.location.href = `/form_builder/${item.Id}`}
                style={{
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    minWidth: '100px'
                }}
            >
                {formatDate(item.CreatedDate)}
            </td>
            <td
                onClick={() => window.location.href = `/form_builder/${item.Id}`}
                style={{
                    cursor: 'pointer',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '150px'
                }}
            >
                {item.CreatedByName}
            </td>
            <td
                className="text-center"
                style={{
                    minWidth: '130px',
                    whiteSpace: 'nowrap'
                }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '5px' }}>
                    <a
                        href={`/form/compile/${item.Id}`}
                        className="text-primary fs-13"
                        title={t('compileForm', { ns: 'labels' })}
                        role="button"
                        style={{
                            display: 'inline-block',
                            padding: '2px 4px',
                            minWidth: '30px'
                        }}
                    >
                        <i className="fa-solid fa-pen-to-square fa-lg"></i>
                    </a>
                    <a
                        href={`/form/assignments/${item.Id}`}
                        className="text-primary fs-13"
                        title={t('assignForm', { ns: 'labels' })}
                        role="button"
                        style={{
                            display: 'inline-block',
                            padding: '2px 4px',
                            minWidth: '30px'
                        }}
                    >
                        <i className="fa-solid fa-user-plus fa-lg"></i>
                    </a>
                    <a
                        className="text-danger fs-13"
                        title={t('delete', { ns: 'labels' })}
                        role="button"
                        onClick={() => handleDeleteForm(item.Id, item.FormName)}
                        style={{
                            display: 'inline-block',
                            padding: '2px 4px',
                            minWidth: '30px',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="fa-solid fa-trash-can fa-lg"></i>
                    </a>
                </div>
            </td>
        </tr>
    );

    // Render the full result table
    const getTable = () => (
        <div className="table-responsive" style={{ overflowX: 'auto' }}>
            <table
                id="formBuilderTable"
                className="table table-striped"
                style={{
                    minWidth: '600px',
                    tableLayout: 'auto'
                }}
            >
                <thead>
                    <tr>
                        <th
                            style={{
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '200px',
                                minWidth: '120px'
                            }}
                        >
                            {t('formId', { ns: 'labels' })}
                        </th>
                        <th
                            style={{
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '200px',
                                minWidth: '120px'
                            }}
                        >
                            {t('formName', { ns: 'labels' })}
                        </th>
                        <th
                            style={{
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '150px',
                                minWidth: '100px'
                            }}
                        >
                            {t('formCode', { ns: 'labels' })}
                        </th>
                        <th
                            style={{
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '150px',
                                minWidth: '100px'
                            }}
                        >
                            {t('formGroup', { ns: 'labels' })}
                        </th>
                        <th
                            style={{
                                whiteSpace: 'nowrap',
                                minWidth: '100px'
                            }}
                        >
                            {t('date', { ns: 'labels' })}
                        </th>
                        <th
                            style={{
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '150px',
                                minWidth: '100px'
                            }}
                        >
                            {t('createdBy', { ns: 'labels' })}
                        </th>
                        <th
                            className="text-center"
                            style={{
                                minWidth: '130px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t('actions', { ns: 'labels' })}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {searchList && searchList.length > 0 ? (
                        searchList.map(getTableRow)
                    ) : (
                        <tr>
                            <td
                                colSpan="6"
                                className="text-center"
                                style={{
                                    padding: '20px',
                                    fontStyle: 'italic'
                                }}
                            >
                                {t('noResults', { ns: 'labels' })}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <>
            {/* Breadcrumb managed by centralized component */}
            <div className="page-titles">
                <ol className="breadcrumb">
                </ol>
            </div>

            <div className="container-fluid">
                <div className="row">
                    <div className="col-xl-12 col-lg-12">
                        <div className="card">
                            {/* Filter inputs */}
                            <div className="card-header">
                                <h4 className="card-title">{t('filter', { ns: 'labels' })}</h4>
                            </div>
                            <div className="card-body">
                                <div className="basic-form row">
                                    {/* Form name input */}
                                    <div className="mb-3 col-md-6">
                                        <label className="form-label">{t('formName', { ns: 'labels' })}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t('enterFormName', { ns: 'labels' })}
                                            value={formName}
                                            onChange={(e) => setFormName(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                        />
                                    </div>

                                    {/* Form code input */}
                                    <div className="mb-3 col-md-6">
                                        <label className="form-label">{t('formCode', { ns: 'labels' })}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t('enterFormCode', { ns: 'labels' })}
                                            value={formCode}
                                            onChange={(e) => setFormCode(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                        />
                                    </div>

                                    {/* Form group dropdown (using Choices component) */}
                                    <div className="mb-3 col-md-6">
                                        <label className="form-label">{t('formGroup', { ns: 'labels' })}</label>
                                        <Choices
                                            voc={groupsVoc}
                                            value="Code"
                                            text="Name"
                                            className="inputStyle blackText bg-white"
                                            selected={formGroup ? [{ Code: formGroup }] : []}
                                            onChange={(e) => {
                                                const selected = e.selected?.[0]?.Code || "";
                                                setFormGroup(selected);
                                            }}
                                            placeholder={t('selectGroup', { ns: 'labels' })}
                                            dropDownParent="root"
                                        />
                                    </div>

                                    {/* Search and clear buttons */}
                                    <div className="mb-3 col-md-12">
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={getFilteredData}
                                            aria-label={t('search', { ns: 'labels' })}
                                        >
                                            {t('search', { ns: 'labels' })}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary ms-2"
                                            onClick={clearData}
                                            aria-label={t('clear', { ns: 'labels' })}
                                        >
                                            {t('clear', { ns: 'labels' })}
                                        </button>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Results table */}
                {searchList && (
                    <div className="row">
                        <div className="col-xl-12 col-lg-12">
                            <div className="card">
                                <div className="card-body p-0">
                                    <div className="active-projects task-table">
                                        <div className="tbl-caption">
                                            <h4 className="heading mb-0 text-start">{t('findedElements', { ns: 'labels' })}</h4>
                                        </div>
                                        {getTable()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export { Form_Builder_Finder };
